// The "Generated files" panel: the download-only files a run holds, with a download
// for each. Nothing writes new ones.
import { useState } from "react";

import styles from "./GeneratedFilesPanel.module.css";

export interface GeneratedFile {
  artifact_id: string;
  filename: string;
  // Set when the file was also saved into a project's files.
  project_id: string | null;
}

interface Props {
  files: GeneratedFile[];
  // Resolves a project id to the name an operator recognises.
  projectLabel: (projectId: string) => string;
  // Mints the presigned URL. Throwing surfaces as an inline error rather than a
  // silent no-op (every mutation gets visible error handling).
  onRequestUrl: (artifactId: string) => Promise<string>;
  // Removes the file for good. Optional: only the surfaces with a delete route
  // behind them offer the button, rather than showing one that cannot work.
  onDelete?: (artifactId: string) => Promise<void>;
}

export const GeneratedFilesPanel = ({
  files,
  projectLabel,
  onRequestUrl,
  onDelete,
}: Props) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (files.length === 0) return null;

  // An agent writes a file because it read the request that way, and it is wrong
  // often enough to need taking back. Confirmed because it is irreversible and
  // reaches further than the panel - window.confirm is what every other
  // destructive action here uses.
  const onRemove = async (file: GeneratedFile) => {
    if (onDelete === undefined) return;
    const alsoProject = file.project_id
      ? `\n\nThis also removes it from ${projectLabel(file.project_id)}'s files.`
      : "";
    if (!window.confirm(`Delete "${file.filename}"?${alsoProject}\n\nThis cannot be undone.`)) {
      return;
    }
    setError(null);
    setBusyId(file.artifact_id);
    try {
      await onDelete(file.artifact_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete that file.");
    } finally {
      setBusyId(null);
    }
  };

  const onDownload = async (artifactId: string) => {
    setError(null);
    setBusyId(artifactId);
    try {
      const url = await onRequestUrl(artifactId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not download that file.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.artifacts}>
      <div className={styles.artifactsHead}>Generated files</div>
      <ul className={styles.artifactList}>
        {files.map((f) => (
          <li key={f.artifact_id} className={styles.artifactItem}>
            <span className={styles.artifactName}>{f.filename}</span>
            {f.project_id && (
              <span className={styles.artifactAttached}>
                saved to {projectLabel(f.project_id)}
              </span>
            )}
            <button
              type="button"
              className={styles.downloadBtn}
              onClick={() => onDownload(f.artifact_id)}
              disabled={busyId !== null}
            >
              {busyId === f.artifact_id ? "Preparing..." : "Download"}
            </button>
            {onDelete !== undefined && (
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={() => onRemove(f)}
                disabled={busyId !== null}
                aria-label={`Delete ${f.filename}`}
              >
                Delete
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};
